import React, { useEffect, useState } from "react";
import { LanguageSwitcher } from "sunday-component";
import AuthModal from "src/components/Auth/AuthModal";
import { useTranslation } from "next-i18next";
import { Row } from "antd";
import { color } from "src/styles/globals";
import { useRouter } from "next/router";
import Image from "next/image";
import { maskProfilePhoneNumber } from "src/utils/maskData";
import { logout } from "src/api/auth";
import { logError } from "src/utils/logger";
import { filterParams, formatPathByLogicalGroupChannel } from "src/utils/url";
import { GLOBAL_PARAMS } from "src/constants/params";
import { formatMobileNumberToInternal } from "src/utils/format";
import { IProfileHeaderProps, Language } from "../IHeader";
import SundayLogo from "../SundayLogo/SundayLogo";
import MainMenu from "./ProfileMainMenu/MainMenu";
import MobileMainMenu from "./ProfileMainMenu/MobileMainMenu";
import {
  ButtonTitle,
  Content,
  Header,
  HeaderContainer,
  Left,
  DesktopLoginButton,
  LoginButtonBody,
  MenuDropdownDisplay,
  MenuDropdownList,
  MenuDropdownOption,
  MenuIcon,
  MenuText,
  Right,
  MenuDropdown,
  MobileLoginButton,
  Drawer,
  DrawerMenuOptionContainer,
  ProfileIconContainer,
  MobileProfileIconContainer,
  LogOutMenuText,
  ProfileNameTitle,
  DrawerMenuDisplayContainer,
  Divider,
  ProfileInfoContainer,
  MobileNumberBody,
  SubMenuContainer,
  SundayLogoContainer,
  NewBadge,
  NewBadgeText,
  DividerSpace,
} from "./ProfileHeader.style";
import ProfileMenuIcon from "../../Icons/ProfileMenuIcon";
import TooltipDesktop from "../../Tooltip/TooltipDesktop";
import TooltipMobile from "../../Tooltip/TooltipMobile";
import { useCustomBreakpointHook } from "../../CustomBreakpointHook";

type ProfileMenuListType = {
  key: string;
  onClick?: () => void;
  type: "option" | "display";
  divider?: boolean;
  space?: boolean;
  render: JSX.Element;
};

const NewMenuBadge = ({ locale }: { locale: string }) => {
  const newText = locale === "th" ? "ใหม่!" : "New!";
  return (
    <NewBadge>
      <NewBadgeText>{newText}</NewBadgeText>
    </NewBadge>
  );
};

const menuDropdownList = (menuList: ProfileMenuListType[]) => {
  return (
    <MenuDropdownList
      bordered
      dataSource={menuList}
      renderItem={(item: ProfileMenuListType) => (
        <>
          {item.divider && <Divider />}
          {item.space && (
            <>
              <DividerSpace />
              <Divider />
            </>
          )}
          {item.type === "option" && (
            <MenuDropdownOption key={item.key} onClick={item.onClick}>
              {item.render}
            </MenuDropdownOption>
          )}
          {item.type === "display" && (
            <MenuDropdownDisplay key={item.key} onClick={item.onClick}>
              {item.render}
            </MenuDropdownDisplay>
          )}
        </>
      )}
    />
  );
};

const menuDrawerList = (menuList: ProfileMenuListType[]) => {
  return (
    <>
      {menuList.map((item) => (
        <>
          {item.divider && <Divider />}
          {item.space && (
            <>
              <DividerSpace />
              <Divider />
            </>
          )}
          {item.type === "option" && (
            <DrawerMenuOptionContainer onClick={item.onClick}>
              {item.render}
            </DrawerMenuOptionContainer>
          )}
          {item.type === "display" && (
            <DrawerMenuDisplayContainer onClick={item.onClick}>
              {item.render}
            </DrawerMenuDisplayContainer>
          )}
        </>
      ))}
      <Divider />
    </>
  );
};

type MenuOptionProps = {
  icon: string;
  displayName: string;
};

const MenuOption = ({ icon, displayName }: MenuOptionProps) => (
  <Row wrap={false}>
    <MenuIcon src={icon} height={24} width={24} />
    <MenuText level={7}>{displayName}</MenuText>
  </Row>
);

const ProfileHeader = ({
  profile,
  isLogin,
  onChangeLanguage,
  language,
  showSwitchLanguage,
  menus,
  content,
}: IProfileHeaderProps): JSX.Element => {
  const router = useRouter();
  const { t } = useTranslation("layout");
  const [subMenuVisible, setSubMenuVisible] = useState(false);
  const [isLoginModalVisible, setIsLoginModalVisible] = useState(false);
  const [isMenuListOpen, setIsMenuListOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const availableLanguage: Language[] = content?.supportedLangs.map((lang) => {
    return lang.code as Language;
  });
  const channelPath =
    typeof router.query.channelPath === "object"
      ? router.query.channelPath?.[0]
      : router.query.channelPath;
  const headerLink = content?.headerLogoLink;
  const displayName =
    profile?.name && profile.name !== ""
      ? profile.name
      : t("header.defaultProfileName");
  const mobileNumber = formatMobileNumberToInternal(
    profile?.contacts.primaryphone
  );
  const authUrl = process.env.NEXT_PUBLIC_AUTH_URL;

  const { fullHeaderSize } = useCustomBreakpointHook({
    fullHeaderSize: 1387,
  });

  useEffect(() => {
    const tooltip = sessionStorage.getItem("newUser");
    setTooltipVisible(!(tooltip === null));
    sessionStorage.removeItem("newUser");
  }, []);
  const handleOpenLoginModal = () => {
    setIsLoginModalVisible(true);
    setIsMenuListOpen(false);
  };

  const handleViewOrder = () => {
    setIsMenuListOpen(false);
    router.push({
      pathname: formatPathByLogicalGroupChannel(`/${channelPath}/user/orders`),
      query: filterParams(router.query, GLOBAL_PARAMS),
    });
  };

  const handleGoToAffiliatePage = () => {
    setIsMenuListOpen(false);
    router.push({
      pathname: formatPathByLogicalGroupChannel(`/${channelPath}/affiliate`),
      query: filterParams(router.query, GLOBAL_PARAMS),
    });
  };

  const handleOnGoToAccountSetting = () => {
    window.open(`${authUrl}/${router.locale}/setting/profile-info`, "_blank");
    setIsMenuListOpen(false);
  };

  const handleLogOut = async () => {
    try {
      await logout();
    } catch (error) {
      logError("[profile-header] fail on logout", error);
    }

    router.reload();
  };

  const handleCloseMenuList = () => {
    setIsMenuListOpen(false);
  };

  const guestMenuOption: ProfileMenuListType[] = [
    {
      key: "login",
      onClick: handleOpenLoginModal,
      type: "option",
      divider: true,
      render: (
        <MenuOption
          icon="/static/profile-menu-login.svg"
          displayName={t("header.profileMenu.login")}
        />
      ),
    },
    {
      key: "signUp",
      onClick: handleOpenLoginModal,
      type: "option",
      divider: true,
      render: (
        <MenuOption
          icon="/static/profile-menu-sign-up.svg"
          displayName={t("header.profileMenu.signUp")}
        />
      ),
    },
    {
      key: "affiliate",
      onClick: handleGoToAffiliatePage,
      type: "option",
      divider: true,
      space: true,
      render: (
        <>
          <MenuOption
            icon="/static/affiliate-icon.svg"
            displayName={t("header.profileMenu.affiliate")}
          />
          <NewMenuBadge locale={router.locale} />
        </>
      ),
    },
  ];

  const profileMenuOption: ProfileMenuListType[] = [
    {
      key: "profile",
      type: "display",
      divider: true,
      render: (
        <Row>
          <MenuIcon
            src="/static/profile-menu-default-user-icon.svg"
            height={48}
            width={48}
          />
          <ProfileInfoContainer fullSize={!fullHeaderSize}>
            <ProfileNameTitle level={6}>{displayName}</ProfileNameTitle>
            <MobileNumberBody level={7}>
              {maskProfilePhoneNumber(mobileNumber)}
            </MobileNumberBody>
          </ProfileInfoContainer>
        </Row>
      ),
    },
    {
      key: "viewOrder",
      onClick: handleViewOrder,
      type: "option",
      divider: true,
      render: (
        <MenuOption
          icon="/static/profile-menu-view-order.svg"
          displayName={t("header.profileMenu.viewOrder")}
        />
      ),
    },
    {
      key: "affiliate",
      onClick: handleGoToAffiliatePage,
      type: "option",
      divider: true,
      render: (
        <>
          <MenuOption
            icon="/static/affiliate-icon.svg"
            displayName={t("header.profileMenu.affiliate")}
          />
          <NewMenuBadge locale={router.locale} />
        </>
      ),
    },
    {
      key: "accountSetting",
      onClick: handleOnGoToAccountSetting,
      type: "option",
      divider: true,
      render: (
        <MenuOption
          icon="/static/profile-menu-account-setting.svg"
          displayName={t("header.profileMenu.accountSetting")}
        />
      ),
    },
    {
      key: "logOut",
      onClick: handleLogOut,
      type: "option",
      divider: true,
      render: (
        <Row>
          <MenuIcon
            src="/static/profile-menu-log-out.svg"
            height={24}
            width={24}
          />
          <LogOutMenuText level={7}>
            {t("header.profileMenu.logout")}
          </LogOutMenuText>
        </Row>
      ),
    },
  ];

  return (
    <>
      <AuthModal
        visible={isLoginModalVisible}
        onClose={setIsLoginModalVisible}
      />
      <Header>
        <HeaderContainer>
          <Content>
            <Left>
              <SundayLogoContainer>
                <SundayLogo
                  content={{
                    headerLogoLink: headerLink,
                    headerLogoImageUrl:
                      "https://static.easysunday.com/retail/artifacts/sunday-logo.svg",
                  }}
                />
              </SundayLogoContainer>
              {fullHeaderSize && (
                <MainMenu
                  subMenuVisible={subMenuVisible}
                  setSubMenuVisible={setSubMenuVisible}
                  menus={menus}
                />
              )}
            </Left>
            <Right>
              <SubMenuContainer>
                {fullHeaderSize && (
                  <>
                    <LanguageSwitcher
                      availableLanguages={availableLanguage}
                      showSwitch={showSwitchLanguage}
                      onChangeLanguage={onChangeLanguage}
                      language={language}
                    />
                    <TooltipDesktop
                      tooltipContent={t("header.tooltip")}
                      tooltipVisible={tooltipVisible && isLogin}
                      setTooltipVisible={() => setTooltipVisible(false)}
                    >
                      <MenuDropdown
                        visible={isMenuListOpen}
                        overlay={
                          isLogin
                            ? menuDropdownList(profileMenuOption)
                            : menuDropdownList(guestMenuOption)
                        }
                        trigger={["click"]}
                        onVisibleChange={(visible) => {
                          // visible menu list for authorized user only
                          setIsMenuListOpen(visible);
                        }}
                        overlayStyle={{
                          width: "274px",
                        }}
                      >
                        <DesktopLoginButton
                          iconExists={isLogin}
                          active={isMenuListOpen || isLoginModalVisible}
                        >
                          <LoginButtonBody>
                            {isLogin && (
                              <ProfileIconContainer>
                                <Image
                                  src="/static/profile-menu-default-user-icon.svg"
                                  height={32}
                                  width={32}
                                />
                              </ProfileIconContainer>
                            )}
                            {isLogin && (
                              <ButtonTitle>{displayName}</ButtonTitle>
                            )}
                            {!isLogin && (
                              <ButtonTitle>
                                {t("header.guestProfileMenuTitle")}
                              </ButtonTitle>
                            )}
                          </LoginButtonBody>
                        </DesktopLoginButton>
                      </MenuDropdown>
                    </TooltipDesktop>
                  </>
                )}
                {!fullHeaderSize && (
                  <>
                    <Drawer
                      closable={false}
                      placement="right"
                      onClose={handleCloseMenuList}
                      visible={isMenuListOpen}
                      width="320"
                      bodyStyle={{
                        padding: "0px",
                      }}
                    >
                      {!isLogin && menuDrawerList(guestMenuOption)}
                      {isLogin && menuDrawerList(profileMenuOption)}
                    </Drawer>
                    <TooltipMobile
                      tooltipContent={t("header.tooltip")}
                      tooltipVisible={tooltipVisible && isLogin}
                      setTooltipVisible={() => setTooltipVisible(false)}
                    >
                      {isLogin && (
                        <MobileProfileIconContainer
                          onClick={() => setIsMenuListOpen(!isMenuListOpen)}
                        >
                          <Image
                            src="/static/profile-menu-default-user-icon.svg"
                            height={40}
                            width={40}
                          />
                        </MobileProfileIconContainer>
                      )}
                      {!isLogin && (
                        <MobileLoginButton
                          type="outlined"
                          onClick={() => setIsMenuListOpen(!isMenuListOpen)}
                        >
                          <ProfileMenuIcon color={color.carrotOrange03} />
                        </MobileLoginButton>
                      )}
                    </TooltipMobile>
                    <MobileMainMenu
                      availableLanguage={availableLanguage}
                      showSwitch={showSwitchLanguage}
                      onChangeLanguage={onChangeLanguage}
                      language={language}
                      menus={menus}
                    />
                  </>
                )}
              </SubMenuContainer>
            </Right>
          </Content>
        </HeaderContainer>
      </Header>
    </>
  );
};

export default ProfileHeader;
